/**
 * Chunked file upload — splits a file into small raw-byte POSTs instead of
 * one big multipart request, so a large Knowledge Base doc or chat
 * attachment doesn't trip Cloud Run's request-size limit (413) the way a
 * single `FormData` POST of the whole file does.
 *
 * Also owns the "warn before refresh/close while an upload is in flight"
 * behavior: a real browser refresh/close can only be intercepted with the
 * native beforeunload dialog (its wording is fixed by the browser, not
 * something we can style into a custom toast) — if the user goes ahead and
 * leaves, navigator.sendBeacon fires a best-effort cleanup call so the
 * backend deletes the partial upload instead of leaving it orphaned until
 * its 24h TTL expiry (backend/src/model/uploadChunkSession.js).
 */
import apiClient from "./apiClient";
import env from "../config/env";

// Comfortably under Cloud Run's request-size ceiling, with headroom under
// the backend's 9MB per-chunk limit (backend/src/routes/chat/routes.js).
const CHUNK_SIZE = 6 * 1024 * 1024;

const UPLOAD_CHUNK_BASE = "/chat/assistants/files/upload-chunk";

// uploadId -> true, for whatever's currently mid-upload in this tab.
const activeUploads = new Map();

const cancelBeaconUrl = (uploadId) =>
  `${env.API_URL}/v1${UPLOAD_CHUNK_BASE}/${uploadId}/cancel-beacon`;

const sendCancelBeacon = (uploadId) => {
  try {
    // text/plain (not application/json) so this stays a CORS-simple request —
    // a cross-origin beacon that needs a preflight isn't guaranteed to
    // complete before the page actually unloads.
    navigator.sendBeacon(
      cancelBeaconUrl(uploadId),
      new Blob([JSON.stringify({ uploadId })], { type: "text/plain" })
    );
  } catch {
    // Best-effort only — the page is unloading, there's no recovering from
    // this failing. The backend TTL index is the fallback.
  }
};

if (typeof window !== "undefined") {
  window.addEventListener("beforeunload", (e) => {
    if (activeUploads.size === 0) return;
    activeUploads.forEach((_, uploadId) => sendCancelBeacon(uploadId));
    // Browsers show their own fixed "leave site?" wording here regardless of
    // returnValue's content — this is what actually triggers that prompt.
    e.preventDefault();
    e.returnValue = "";
  });
}

/**
 * @param {File} file
 * @param {{profileId: string, username?: string, assistantIds?: string[], compType: string, signal?: AbortSignal}} opts
 * @returns {Promise<{success: boolean, message?: string, data?: {file_id: string, fileName: string, results: object[]}}>}
 */
export async function uploadFileInChunks(file, { profileId, username, assistantIds = [], compType, signal } = {}) {
  const totalChunks = Math.max(1, Math.ceil(file.size / CHUNK_SIZE));

  const initResponse = await apiClient.post(
    `${UPLOAD_CHUNK_BASE}/init`,
    {
      fileName: file.name,
      mimeType: file.type,
      fileSize: file.size,
      totalChunks,
      profile_id: profileId,
      username,
      assistant_ids: assistantIds,
      compType,
    },
    { signal }
  );

  const uploadId = initResponse?.data?.data?.uploadId;
  if (!uploadId) {
    throw new Error(initResponse?.data?.message || "Failed to start upload");
  }

  activeUploads.set(uploadId, true);

  try {
    for (let index = 0; index < totalChunks; index++) {
      const start = index * CHUNK_SIZE;
      const chunk = file.slice(start, start + CHUNK_SIZE);
      await apiClient.post(`${UPLOAD_CHUNK_BASE}/${uploadId}/chunk/${index}`, chunk, {
        headers: { "Content-Type": "application/octet-stream" },
        signal,
      });
    }

    const completeResponse = await apiClient.post(`${UPLOAD_CHUNK_BASE}/${uploadId}/complete`, {}, { signal });
    return completeResponse.data;
  } catch (error) {
    // Don't leave orphaned chunks behind for a failed/aborted upload — fire
    // and forget; if this also fails, the TTL index cleans it up within 24h.
    apiClient.delete(`${UPLOAD_CHUNK_BASE}/${uploadId}`).catch(() => {});
    throw error;
  } finally {
    activeUploads.delete(uploadId);
  }
}
